import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Edit2, Trash2, Bus, Clock, IndianRupee, Settings, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import DateInput from '../../components/common/DateInput';
import { formatCurrency, generateId } from '../../utils/helpers';
import { api } from '../../utils/api';

function haversineKm(lat1, lon1, lat2, lon2) {
  const toR = d => d * Math.PI / 180;
  const dLat = toR(lat2 - lat1), dLon = toR(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLon/2)**2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function fmtDist(km) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

const emptyStop = () => ({ name: '', time: '', fee: '', lat: '', lng: '' });
const emptyForm = () => ({ routeName: '', startPoint: 'School', endPoint: '', vehicleId: '', driverId: '', stops: [emptyStop()] });

const inputCls  = (err) => `w-full px-3 py-1 border rounded-lg text-sm focus:outline-none focus:ring-2 ${err ? 'border-red-400 focus:ring-red-300 bg-red-50' : 'border-gray-300 focus:ring-purple-300'}`;
const baseCls   = 'w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300';
const Err = ({ msg }) => msg ? <p className="text-red-500 text-xs mt-0.5">{msg}</p> : null;

function validate(form) {
  const errs = {};
  if (!form.routeName?.trim())   errs.routeName   = 'Route name is required';
  if (!form.startPoint?.trim())  errs.startPoint  = 'Start point is required';
  if (!form.endPoint?.trim())    errs.endPoint    = 'End point is required';
  if (!form.vehicleId)           errs.vehicleId   = 'Select a vehicle';
  if (!form.driverId)            errs.driverId    = 'Select a driver';

  const validStops = form.stops.filter(s => s.name.trim());
  if (validStops.length === 0) {
    errs.stops = 'Add at least one pickup point';
  } else {
    errs.stopRows = form.stops.map(s => {
      if (!s.name.trim()) return null;
      const row = {};
      if (!s.time)                           row.time = 'Required';
      if (!s.fee || Number(s.fee) <= 0)      row.fee  = 'Enter fee > 0';
      return Object.keys(row).length ? row : null;
    });
  }
  return errs;
}

// ── Transport Fee Terms settings panel ──────────────────────────────────────

const emptyTerm = (n) => ({ termNo: n, label: `Term ${n}`, percent: '', dueDate: '' });

function TermsPanel({ transportTerms, setTransportTerms }) {
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState(() =>
    transportTerms.length > 0 ? transportTerms.map(t => ({ ...t })) : []
  );
  const [enabled, setEnabled] = useState(transportTerms.length > 0);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const totalPct = terms.reduce((s, t) => s + (Number(t.percent) || 0), 0);
  const pctOk    = totalPct === 100;

  const addTerm    = () => setTerms(ts => [...ts, emptyTerm(ts.length + 1)]);
  const removeTerm = (i) => setTerms(ts => ts.filter((_, idx) => idx !== i).map((t, idx) => ({ ...t, termNo: idx + 1 })));
  const updateTerm = (i, key, val) => setTerms(ts => ts.map((t, idx) => idx === i ? { ...t, [key]: val } : t));

  const save = async () => {
    if (enabled && !pctOk) return;
    setSaving(true); setSaveError('');
    const payload = enabled ? terms.map(t => ({ ...t, percent: Number(t.percent) })) : [];
    const ok = await setTransportTerms(payload);
    setSaving(false);
    if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    else { setSaveError('Save failed — check your connection or contact support.'); }
  };

  const toggle = (next) => {
    setEnabled(next);
    if (next && terms.length === 0) setTerms([emptyTerm(1), emptyTerm(2), emptyTerm(3)]);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold hover:bg-gray-50 rounded-xl transition-colors"
      >
        <span className="flex items-center gap-2">
          <Settings size={15} className="text-purple-600" />
          Transport Fee Collection Terms
          {transportTerms.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              {transportTerms.length} term{transportTerms.length !== 1 ? 's' : ''} configured
            </span>
          )}
        </span>
        {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Split the annual transport fee into installment terms — applies to all routes.
            </p>
            <label className="flex items-center gap-2 cursor-pointer select-none shrink-0 ml-4">
              <span className="text-sm font-medium">{enabled ? 'Enabled' : 'Disabled'}</span>
              <div
                onClick={() => toggle(!enabled)}
                className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${enabled ? 'bg-purple-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
            </label>
          </div>

          {enabled && (
            <>
              {/* Column headers */}
              <div className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-3 text-xs font-medium text-gray-500 px-1">
                <span className="w-6">#</span>
                <span>Term Label</span>
                <span>% of Annual Fee <span className="text-red-400">*</span></span>
                <span>Due Date</span>
                <span></span>
              </div>

              <div className="space-y-2">
                {terms.map((t, i) => (
                  <div key={i} className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-3 items-center">
                    <span className="w-6 text-xs text-gray-400 text-center">{i + 1}</span>
                    <input
                      value={t.label}
                      onChange={e => updateTerm(i, 'label', e.target.value)}
                      placeholder={`e.g. Term ${i + 1}`}
                      className={baseCls}
                    />
                    <div className="relative">
                      <input
                        type="number"
                        min="1" max="100"
                        value={t.percent}
                        onChange={e => updateTerm(i, 'percent', e.target.value)}
                        placeholder="e.g. 40"
                        className={`w-full pl-3 pr-7 py-1 border rounded-lg text-sm focus:outline-none focus:ring-2 ${!pctOk && t.percent ? 'border-orange-400 focus:ring-orange-300' : 'border-gray-300 focus:ring-purple-300'}`}
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                    </div>
                    <DateInput
                      value={t.dueDate || ''}
                      onChange={e => updateTerm(i, 'dueDate', e.target.value)}
                      className={baseCls}
                      placeholder="dd/mm/yyyy"
                    />
                    <button
                      type="button"
                      onClick={() => removeTerm(i)}
                      disabled={terms.length === 1}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <button type="button" onClick={addTerm}
                  className="flex items-center gap-1 text-purple-600 hover:text-purple-800 text-sm font-medium">
                  <Plus size={13} /> Add Term
                </button>
                <div className={`text-sm font-semibold px-3 py-1 rounded-lg ${pctOk ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                  Total: {totalPct}% {pctOk ? '✓' : `(needs ${100 - totalPct > 0 ? `+${100 - totalPct}` : 100 - totalPct}%)`}
                </div>
              </div>
            </>
          )}

          {saveError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving || (enabled && !pctOk)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${saving || (enabled && !pctOk) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white'}`}
            >
              <Save size={14} /> {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Terms'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

function RouteList() {
  const { routes, vehicles, staff, saveRoute, updateRoute, deleteRoute, transportTerms, setTransportTerms } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [schoolLoc, setSchoolLoc] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [errs, setErrs]   = useState({});

  useEffect(() => {
    if (isAdmin) {
      api.getSchoolSettings().then(d => {
        if (d.lat && d.lng) setSchoolLoc({ lat: parseFloat(d.lat), lng: parseFloat(d.lng) });
      }).catch(() => {});
    }
  }, [isAdmin]);

  const openAdd = () => { setForm(emptyForm()); setEditing(null); setErrs({}); setModal(true); };
  const openEdit = (r) => {
    setForm({ ...r, stops: (r.stops || []).length > 0 ? r.stops : [emptyStop()] });
    setEditing(r); setErrs({}); setModal(true);
  };
  const remove = async (id) => { if (window.confirm('Delete route?')) await deleteRoute(id); };

  const save = async () => {
    const v = validate(form);
    const hasStopErr = (v.stopRows || []).some(Boolean);
    if (Object.keys(v).filter(k => k !== 'stopRows').length || hasStopErr) {
      setErrs(v);
      return;
    }
    const payload = { ...form, stops: form.stops.filter(s => s.name.trim()) };
    if (editing) await updateRoute(editing.id, { ...payload, id: editing.id });
    else await saveRoute({ ...payload, id: generateId('RTE') });
    setModal(false);
  };

  const updateStop = (i, field, val) => {
    setForm(f => ({ ...f, stops: f.stops.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
    if (errs.stopRows?.[i]) {
      setErrs(e => {
        const rows = [...(e.stopRows || [])];
        if (rows[i]) { const r = { ...rows[i] }; delete r[field]; rows[i] = Object.keys(r).length ? r : null; }
        return { ...e, stopRows: rows };
      });
    }
  };
  const addStop    = () => { setForm(f => ({ ...f, stops: [...f.stops, emptyStop()] })); setErrs(e => ({ ...e, stops: undefined })); };
  const removeStop = (i) => setForm(f => ({ ...f, stops: f.stops.filter((_, idx) => idx !== i) }));

  const [geocoding, setGeocoding] = React.useState({});
  const geocodeStopName = async (i, name) => {
    if (!name.trim()) return;
    setGeocoding(g => ({ ...g, [i]: true }));
    try {
      const q = encodeURIComponent(name.trim() + ' India');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`, {
        headers: { 'User-Agent': 'VasavyaSMS/1.0' }
      });
      const data = await res.json();
      if (data[0]) {
        updateStop(i, 'lat', parseFloat(data[0].lat).toFixed(6));
        updateStop(i, 'lng', parseFloat(data[0].lon).toFixed(6));
      } else {
        // Only alert if no coordinates already entered — if user typed coords manually, stay silent
        const stop = form.stops[i];
        if (!stop?.lat || !stop?.lng) {
          alert(`Could not find "${name}" on map. Please type the lat/lng from Google Maps manually.`);
        }
      }
    } catch { /* silently fail — user can always type coordinates manually */ }
    finally { setGeocoding(g => ({ ...g, [i]: false })); }
  };

  const getDriver  = (id) => { const s = staff.find(s => s.id === id); return s ? `${s.firstName} ${s.lastName}` : '—'; };
  const getVehicle = (id) => vehicles.find(v => v.id === id)?.registrationNumber || '—';

  const hasTerms = transportTerms.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Transport Routes</h1>
          <p className="text-gray-500 text-sm mt-0.5">{routes.length} route{routes.length !== 1 ? 's' : ''} configured</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1 rounded-lg text-sm font-medium">
          <Plus size={16} /> Add Route
        </button>
      </div>

      {/* Term settings panel */}
      <TermsPanel transportTerms={transportTerms} setTransportTerms={setTransportTerms} />

      {routes.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100 text-gray-400">No transport routes configured yet.</div>
      )}

      <div className="grid gap-4">
        {routes.map(route => {
          const stops = (route.stops || []).filter(s => s.name);
          return (
            <div key={route.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Card header */}
              <div className="flex items-start justify-between px-5 py-3 border-b border-gray-100">
                <div>
                  <h3 className="font-bold text-base">{route.routeName || route.name}</h3>
                  <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin size={12} />{route.startPoint} → {route.endPoint || '—'}</span>
                    <span className="flex items-center gap-1"><Bus size={12} />{getVehicle(route.vehicleId)}</span>
                    <span>Driver: {getDriver(route.driverId)}</span>
                    <span className="text-purple-600 font-medium">{stops.length} pickup point{stops.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(route)} className="p-1.5 hover:bg-purple-50 rounded-lg text-purple-600"><Edit2 size={15} /></button>
                  <button onClick={() => remove(route.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>

              {/* Pickup points table */}
              {stops.length > 0 ? (
                <div className="px-5 py-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="pb-1.5 font-medium w-6">#</th>
                        <th className="pb-1.5 font-medium">Pickup Point</th>
                        <th className="pb-1.5 font-medium w-24">Time</th>
                        <th className="pb-1.5 font-medium text-right w-28">Annual Fee</th>
                        {isAdmin && schoolLoc && <th className="pb-1.5 font-medium text-right w-24 pl-4">Distance</th>}
                        {hasTerms && transportTerms.map(t => (
                          <th key={t.termNo} className="pb-1.5 font-medium text-right whitespace-nowrap pl-4">
                            {t.label}
                            {t.dueDate && <span className="block text-gray-300 font-normal" style={{fontSize:'10px'}}>Due: {t.dueDate.slice(8,10)}/{t.dueDate.slice(5,7)}/{t.dueDate.slice(0,4)}</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stops.map((stop, i) => {
                        const dist = isAdmin && schoolLoc && stop.lat && stop.lng
                          ? haversineKm(schoolLoc.lat, schoolLoc.lng, parseFloat(stop.lat), parseFloat(stop.lng))
                          : null;
                        return (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                          <td className="py-1.5 text-gray-400 text-xs">{i + 1}</td>
                          <td className="py-1.5 flex items-center gap-1.5">
                            <MapPin size={11} className="text-purple-400 shrink-0" />
                            <span className="font-medium">{stop.name}</span>
                          </td>
                          <td className="py-1.5">
                            {stop.time ? (
                              <span className="flex items-center gap-1 text-gray-500"><Clock size={11} />{stop.time}</span>
                            ) : '—'}
                          </td>
                          <td className="py-1.5 text-right">
                            {Number(stop.fee) > 0
                              ? <span className="font-semibold text-purple-700">{formatCurrency(stop.fee)}</span>
                              : <span className="text-gray-300">—</span>
                            }
                          </td>
                          {isAdmin && schoolLoc && (
                            <td className="py-1.5 text-right pl-4">
                              {dist != null
                                ? <span className="text-xs font-medium text-blue-600">{fmtDist(dist)}</span>
                                : <span className="text-gray-300 text-xs">—</span>}
                            </td>
                          )}
                          {hasTerms && transportTerms.map(t => {
                            const amt = Number(stop.fee) > 0 ? (Number(stop.fee) * t.percent / 100) : 0;
                            return (
                              <td key={t.termNo} className="py-1.5 text-right pl-4">
                                {amt > 0
                                  ? <span className="font-medium text-gray-700">{formatCurrency(amt)}</span>
                                  : <span className="text-gray-300">—</span>
                                }
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                    {hasTerms && stops.some(s => Number(s.fee) > 0) && (
                      <tfoot>
                        <tr className="border-t-2 border-gray-100 text-xs text-gray-400">
                          <td colSpan={isAdmin && schoolLoc ? 5 : 4} className="pt-1.5 text-right font-medium">Fee split:</td>
                          {transportTerms.map(t => (
                            <td key={t.termNo} className="pt-1.5 text-right pl-4 font-medium text-purple-600">{t.percent}%</td>
                          ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              ) : (
                <div className="px-5 py-3 text-sm text-gray-400">No pickup points added.</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Edit Route' : 'Add Route'} size="lg">
        <div className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Route Name <span className="text-red-400">*</span></label>
              <input value={form.routeName || ''}
                onChange={e => { setForm(f => ({ ...f, routeName: e.target.value })); setErrs(v => ({ ...v, routeName: undefined })); }}
                className={inputCls(errs.routeName)} placeholder="e.g. Route A — Kukatpally" />
              <Err msg={errs.routeName} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Point <span className="text-red-400">*</span></label>
              <input value={form.startPoint || ''}
                onChange={e => { setForm(f => ({ ...f, startPoint: e.target.value })); setErrs(v => ({ ...v, startPoint: undefined })); }}
                className={inputCls(errs.startPoint)} placeholder="e.g. School" />
              <Err msg={errs.startPoint} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Point <span className="text-red-400">*</span></label>
              <input value={form.endPoint || ''}
                onChange={e => { setForm(f => ({ ...f, endPoint: e.target.value })); setErrs(v => ({ ...v, endPoint: undefined })); }}
                className={inputCls(errs.endPoint)} placeholder="e.g. Miyapur" />
              <Err msg={errs.endPoint} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vehicle <span className="text-red-400">*</span></label>
              <select value={form.vehicleId || ''}
                onChange={e => { setForm(f => ({ ...f, vehicleId: e.target.value })); setErrs(v => ({ ...v, vehicleId: undefined })); }}
                className={inputCls(errs.vehicleId)}>
                <option value="">Select Vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.registrationNumber} ({v.type})</option>)}
              </select>
              <Err msg={errs.vehicleId} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Driver</label>
              <select value={form.driverId || ''}
                onChange={e => { setForm(f => ({ ...f, driverId: e.target.value })); setErrs(v => ({ ...v, driverId: undefined })); }}
                className={baseCls}>
                <option value="">— Select Driver —</option>
                {(() => {
                  const drivers = staff.filter(s => s.designation === 'Driver');
                  const others  = staff.filter(s => s.designation !== 'Driver');
                  return [
                    ...drivers.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} (Driver)</option>),
                    drivers.length > 0 && others.length > 0 && <option key="sep" disabled>──────────</option>,
                    ...others.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}{s.designation ? ` — ${s.designation}` : ''}</option>),
                  ].filter(Boolean);
                })()}
              </select>
            </div>
          </div>

          {/* Pickup Points */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-sm font-semibold">Pickup Points <span className="text-red-400">*</span></label>
                {errs.stops && <p className="text-red-500 text-xs mt-0.5">{errs.stops}</p>}
              </div>
              <button type="button" onClick={addStop}
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 text-sm font-medium">
                <Plus size={14} /> Add Point
              </button>
            </div>

            <div className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 px-1 mb-1">
              <span>Pickup Point Name <span className="text-red-400">*</span></span>
              <span>Pickup Time <span className="text-red-400">*</span></span>
              <span className="flex items-center gap-0.5"><IndianRupee size={10} />Annual Fee <span className="text-red-400">*</span></span>
              <span></span>
            </div>

            <div className="space-y-1.5">
              {form.stops.map((stop, i) => {
                const rowErr = errs.stopRows?.[i];
                const hasName = stop.name.trim();
                return (
                  <div key={i}>
                    <div className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-2 items-start">
                      <div>
                        <input
                          placeholder={`e.g. Stop ${i + 1}`}
                          value={stop.name}
                          onChange={e => updateStop(i, 'name', e.target.value)}
                          className={inputCls(errs.stops && !hasName)}
                        />
                        {/* Coordinates row */}
                        <div className="flex items-center gap-1 mt-1">
                          <input
                            placeholder="Lat"
                            value={stop.lat || ''}
                            onChange={e => updateStop(i, 'lat', e.target.value)}
                            className="w-full px-2 py-0.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-300 text-gray-600"
                          />
                          <input
                            placeholder="Lng"
                            value={stop.lng || ''}
                            onChange={e => updateStop(i, 'lng', e.target.value)}
                            className="w-full px-2 py-0.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-300 text-gray-600"
                          />
                          <button type="button"
                            onClick={() => geocodeStopName(i, stop.name)}
                            disabled={!hasName || geocoding[i]}
                            title="Auto-detect coordinates from stop name"
                            className="flex-shrink-0 px-1.5 py-0.5 text-xs rounded border border-purple-300 text-purple-600 hover:bg-purple-50 disabled:opacity-40 whitespace-nowrap">
                            {geocoding[i] ? '…' : '📍'}
                          </button>
                        </div>
                        {stop.lat && stop.lng && (
                          <p className="text-xs text-green-600 mt-0.5 flex items-center gap-0.5">
                            ✓ {parseFloat(stop.lat).toFixed(4)}, {parseFloat(stop.lng).toFixed(4)}
                          </p>
                        )}
                        {hasName && !stop.lat && (
                          <p className="text-xs text-amber-500 mt-0.5">⚠ No coords — tap 📍 to auto-detect</p>
                        )}
                      </div>
                      <div>
                        <input
                          type="time"
                          value={stop.time}
                          onChange={e => updateStop(i, 'time', e.target.value)}
                          className={inputCls(hasName && rowErr?.time)}
                        />
                        {hasName && rowErr?.time && <p className="text-red-500 text-xs mt-0.5">{rowErr.time}</p>}
                      </div>
                      <div>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                          <input
                            type="number"
                            placeholder="0"
                            value={stop.fee}
                            onChange={e => updateStop(i, 'fee', e.target.value)}
                            className={`w-full pl-6 pr-2 py-1 border rounded-lg text-sm focus:outline-none focus:ring-2 ${hasName && rowErr?.fee ? 'border-red-400 focus:ring-red-300 bg-red-50' : 'border-gray-300 focus:ring-purple-300'}`}
                          />
                        </div>
                        {hasName && rowErr?.fee && <p className="text-red-500 text-xs mt-0.5">{rowErr.fee}</p>}
                        {hasTerms && Number(stop.fee) > 0 && (
                          <p className="text-xs text-purple-500 mt-0.5">
                            {transportTerms.map(t => `${t.label}: ${formatCurrency(Number(stop.fee) * t.percent / 100)}`).join(' · ')}
                          </p>
                        )}
                      </div>
                      <button type="button" onClick={() => removeStop(i)}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-0.5"
                        disabled={form.stops.length === 1}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {form.stops.filter(s => s.name.trim() && Number(s.fee) > 0).length > 0 && (
              <div className="mt-2 bg-purple-50 rounded-lg px-3 py-1.5 flex justify-between items-center text-sm">
                <span className="text-purple-700 font-medium">Fee range</span>
                <span className="text-purple-800 font-bold">
                  {formatCurrency(Math.min(...form.stops.filter(s => Number(s.fee) > 0).map(s => Number(s.fee))))}
                  {' — '}
                  {formatCurrency(Math.max(...form.stops.filter(s => Number(s.fee) > 0).map(s => Number(s.fee))))}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Route</button>
        </div>
      </Modal>
    </div>
  );
}
export default RouteList;
